import React, { useEffect, useState } from "react";
import { get } from "../../../utils/api";
import MultiColumns from "./MultiColumns";
import { companyId } from "../../../utils/companyId";
import { Typography } from "@mui/material";

const PageBreak = () => <div className="page-break" />;

const Environment = ({ isEditing }) => {
  const [sections, setSections] = useState([]);

  const [data, setData] = useState();

  useEffect(() => {
    const fetchAllData = async () => {
      try {
        const [
          energyRes,
          ghgRes,
          waterRes,
          wasteRes,
          bioRes,
          pollutionRes,
          sustainableRes,
        ] = await Promise.allSettled([
          get(`environment/energy-management/${companyId}`),
          get(`environment/ghg-emissions/${companyId}`),
          get(`environment/water-management/${companyId}`),
          get(`environment/waste-management/${companyId}`),
          get(`environment/biodiversity-and-land-use/${companyId}`),
          get(`environment/pollution/${companyId}`),
          get(`environment/sustainable-sourcing-and-supply-chain/${companyId}`),
        ]);

        const safeData = (result, key) =>
          result.status === "fulfilled" ? result.value?.data?.[key] || {} : {};

        setData({
          energy: safeData(energyRes, "energyData"),
          ghg: safeData(ghgRes, "ghgEmissionsData"),
          water: safeData(waterRes, "waterData"),
          waste: safeData(wasteRes, "wasteData"),
          biodiversity: safeData(bioRes, "biodiversityData"),
          pollution: safeData(pollutionRes, "pollutionData"),
          sustainable: safeData(sustainableRes, "sustainableSourcingData"),
        });
      } catch (error) {
        console.error("Unexpected error in fetchAllData:", error);
      }
    };

    fetchAllData();
  }, []);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const sectionConfigs = [
          {
            title: "ENERGY MANAGEMENT",
            data: data?.energy,
            url: "/environment/energy-management",
            questionary: [
              {
                question: "Total Energy Consumption",
                key: "totalEnergyConsumption.value",
                key2: "totalEnergyConsumption.unit",
              },
              {
                question: "Renewable Energy Consumption",
                key: "renewableEnergyConsumption.value",
                key2: "renewableEnergyConsumption.unit",
              },
              {
                question: "Energy Intensity",
                key: "energyIntensity.value",
                key2: "energyIntensity.unit",
              },
              // {
              //   question: "Energy Efficiency Improvements",
              //   key: "energyEfficiencyImprovements.value",
              //   key2: "energyEfficiencyImprovements.unit",
              // },
            ],
            columns: 2,
          },
          {
            title: "GREENHOUSE GAS EMISSIONS",
            data: data?.ghg,
            url: "/environment/ghg-emissions",
            questionary: [
              {
                gridSize: { xs: 12 },
                question: "Total GHG Emissions",
              },
              {
                question: "Scope 1",
                key: "scope1.value",
                key2: "scope1.unit",
                gridSize: { xs: 12, sm: 6, md: 4 },
              },
              {
                question: "Scope 2",
                key: "scope2.value",
                key2: "scope2.unit",
                gridSize: { xs: 12, sm: 6, md: 4 },
              },
              {
                question: "Scope 3",
                key: "scope3.value",
                key2: "scope3.unit",
                gridSize: { xs: 12, sm: 6, md: 4 },
              },
              {
                question: "GHG Emissions Intensity",
                key: "ghgEmissionIntensity.value",
                key2: "ghgEmissionIntensity.unit",
              },
              // {
              //   question: "Reduction in GHG Emissions",
              //   key: "reductionInGhgEmissions.value",
              //   key2: "reductionInGhgEmissions.unit",
              // },
              {
                question: "Carbon Offsetting",
                key: "carbonOffsetting.value",
                key2: "carbonOffsetting.unit",
              },
            ],
            columns: 3,
          },
          {
            title: "WATER MANAGEMENT",
            data: data?.water,
            url: "/environment/water-management",
            questionary: [
              {
                question: "Total Water Withdrawal",
                key: "totalWaterWithdrawal.value",
                key2: "totalWaterWithdrawal.unit",
              },
              {
                question: "Total Water Discharged",
                key: "totalWaterDischarged.value",
                key2: "totalWaterDischarged.unit",
              },
              {
                question: "Total Waste Water Treated",
                key: "totalWasteWaterTreated.value",
                key2: "totalWasteWaterTreated.unit",
              },
              {
                question: "Total Water Recycled/Reused",
                key: "totalWaterRecycled.value",
                key2: "totalWaterRecycled.unit",
              },
              {
                question: "Water Intensity",
                key: "waterIntensity.value",
                key2: "waterIntensity.unit",
              },
              {
                question: "Water Recycled and Reused",
                key: "waterRecycled.value",
                key2: "waterRecycled.unit",
              },
              {
                question: "Water Discharge Quality",
                key: "waterDischargeQuality.value",
                key2: "waterDischargeQuality.unit",
              },
            ],
            columns: 3,
          },
          {
            title: "waste MANAGEMENT ",
            data: data?.waste,
            url: "/environment/waste-management",
            questionary: [
              {
                question: "Total Waste Generated",
                key: "totalWasteGenerated.value",
                key2: "totalWasteGenerated.unit",
              },
              {
                question: "Waste Intensity",
                key: "wasteIntensity.value",
                key2: "wasteIntensity.unit",
              },
              {
                question: "Waste Diverted from Landfill",
                key: "wasteDivertedFromLandFill.value",
                key2: "wasteDivertedFromLandFill.unit",
              },
              {
                question: "Hazardous Waste Management",
                key: "hazardousWasteManagement.value",
                key2: "hazardousWasteManagement.unit",
              },
            ],
            columns: 2,
          },
          {
            title: "biodiversity topics",
            data: data?.biodiversity,
            url: "/environment/biodiversity",
            questionary: [
              {
                question: "Impact on Biodiversity",
                key: "impactOnBiodiversity.value",
                key2: "impactOnBiodiversity.unit",
                gridSize: { xs: 12 },
              },
              {
                mainheading: "Initiatives for Biodiversity Preservation",
              },
              {
                question: "Number of Projects",
                key: "initiativesForBiodiversityPreservation.numberOfProjects",
                gridSize: { xs: 12, sm: 6, md: 6 },
              },
              {
                question: "Scope of Projects",
                key: "initiativesForBiodiversityPreservation.scopeOfProjects",
                gridSize: { xs: 12, sm: 6, md: 6 },
              },
              {
                question: "Land Rehabilitation",
                key: "landRehabilitation.value",
                key2: "landRehabilitation.unit",
              },
            ],
            // columns: 2,
          },
          {
            title: "Pollution & environmental compliance",
            data: data?.pollution,
            url: "/environment/pollution",
            questionary: [
              {
                mainheading: "Air Emissions (in tCO2e)",
                gridSize: { xs: 12 },
              },
              {
                question: "NOX",
                key: "airEmissions.nox.value",
                gridSize: { xs: 12, sm: 6, md: 2.4 },
              },
              {
                question: "SOX",
                key: "airEmissions.sox.value",
                gridSize: { xs: 12, sm: 6, md: 2.4 },
              },
              {
                question: "VOCS",
                key: "airEmissions.vocs.value",
                gridSize: { xs: 12, sm: 6, md: 2.4 },
              },
              {
                question: "PM 2.5",
                key: "airEmissions.pm25.value",
                gridSize: { xs: 12, sm: 6, md: 2.4 },
              },
              {
                question: "PM 10",
                key: "airEmissions.pm10.value",
                gridSize: { xs: 12, sm: 6, md: 2.4 },
              },
              {
                mainheading: "Compliance with Environmental Regulations",
              },
              {
                question: "Number of fines paid",
                key: "complianceWithEnvironmentalRegulations",
                subkey: "finesPaid",
                gridSize: { xs: 12, sm: 6, md: 6 },
              },
              {
                question: "Regulations ",
                key: "complianceWithEnvironmentalRegulations",
                subkey: "regulations",
                gridSize: { xs: 12, sm: 6, md: 6 },
              },
              {
                mainheading: "Spills and Accidents",
              },
              {
                question: "Number of Accidents",
                key: "spillsAndAccidents",
                subkey: "accidents",
                gridSize: { xs: 12, sm: 6, md: 6 },
              },
              {
                question: "Spills",
                key: "spillsAndAccidents",
                subkey: "spills",
                gridSize: { xs: 12, sm: 6, md: 6 },
              },
            ],
          },
          {
            title: "SUSTAINABLE Sourcing & SUPPLY CHAIN",
            data: data?.sustainable,
            url: "/environment/sustainable-sourcing",
            questionary: [
              {
                question: "Percentage of Sustainable Materials Used",
                key: "percentageOfSustainableMaterialUsed.value",
                key2: "percentageOfSustainableMaterialUsed.unit",
              },
              {
                mainheading: "Supplier Environmental Assessments",
              },
              {
                key: "supplierEnvironmentalAssesments.number",
                question: "Number of suppliers assessed",
                gridSize: { xs: 12, sm: 6, md: 6 },
              },
              {
                key: "supplierEnvironmentalAssesments.percentage",
                question: "% of suppliers assessed",
                gridSize: { xs: 12, sm: 6, md: 6 },
              },
              {
                mainheading: "Initiatives for Sustainable Sourcing",
              },
              {
                question: "Number of Projects",
                key: "initiativesforSustainableSourcing.number",
                gridSize: { xs: 12, sm: 6, md: 6 },
              },
              {
                question: "Impact of Projects",
                key: "initiativesforSustainableSourcing.impact",
                gridSize: { xs: 12, sm: 6, md: 6 },
              },
            ],
          },
        ];

        const sectionResults = [];

        for (const config of sectionConfigs) {
          sectionResults.push({
            title: config.title,
            questionary: config.questionary,
            columns: config.columns,
            url: config.url,
            data: config?.data || {},
          });
        }

        setSections(sectionResults);
      } catch (error) {
        console.error("Error fetching section data:", error);
      }
    };

    fetchData();
  }, [data]);

  return (
    <>
      <Typography
        sx={{
          color: "#488257",
          fontWeight: 600,
          fontSize: "20px",
          lineHeight: "24px",
          textTransform: "uppercase",
          fontFamily: "inter",
          mt: "1rem",
        }}
      >
        BASELINE : Environment
      </Typography>
      {sections.map((section, index) => (
        <React.Fragment key={index}>
          {[3, 5].includes(index) && <PageBreak />}
          <MultiColumns
            key={index}
            title={section.title}
            questionary={section.questionary}
            columns={section.columns}
            url={section.url}
            data={section.data}
            isEditing={isEditing}
          />
        </React.Fragment>
      ))}
    </>
  );
};

export default Environment;
